<template>
  <div>
    <page-not-found v-if="pageNotFound"></page-not-found>
    <page-forbidden :message="pageForbiddenMessage" v-else-if="pageForbidden" />
    <div v-else-if="status == USER_STATUS.LOGGED_IN" class="container-fluid">
      <key-map v-model="showKeyboardShortcuts"></key-map>
      <div class="row">
        <sidebar
          ref="sidebar"
          class="d-none d-lg-flex"
          :org-id="orgId"
          v-if="showSidebar"
          :show-hide-icon="true"
        />
        <main id="main" class="col vh-100 px-0 d-flex flex-column">
          <div class="scrolling-box flex-grow-1" id="main-scrolling">
            <top-img-bar />
            <demo-org-bar :org-id="orgId" v-if="org.isDemoOrg" />
            <need-help-bar :org-id="orgId" v-else />
            <router-view class="app-content-region" />
          </div>
          <modals-container :org-id="orgId" />
        </main>
        <toast-container />
        <dialog-container />
        <spotlight-search ref="spotlightSearch" v-model="showSpotlightSearch" :org-id="orgId" />
        <announcements v-if="hasNewAnnouncements" />
        <sub-org-switch-handler />
        <quick-add-modal
          :payload="addInitiativePayload"
          v-model="quickAddModalShown"
          :org-id="orgId"
          v-if="quickAddModalMounted"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DemoOrgBar from '@/components/demoOrg/DemoOrgBar.vue'
import DialogContainer from '@/components/DialogContainer.vue'
import QuickAddModal from '@/components/initiative/quick-add/QuickAddModal'
import ModalsContainer from '@/components/ModalsContainer'
import NeedHelpBar from '@/components/NeedHelpBar.vue'
import SubOrgSwitchHandler from '@/components/SubOrgSwitchHandler'
import events from '@/constants/events'
import { goalsTabIds } from '@/constants/goals'
import { KEY_CODES } from '@/constants/keyCodes'
import EventBusMixin from '@/mixins/EventBusMixin'
import UserListsMixin from '@/mixins/UserListsMixin'
import TeamMixin from '@/mixins/TeamMixin'
import { USER_STATUS } from '@/store/store'
import {
  getInboxPageRouteConfig,
  getTeamPageRouteConfig,
  getYourWorkPageRouteConfig
} from '@/utils/navigationConfigs'
import TopImgBar from '@/views/TopImgBar'
import * as R from 'ramda'
import KeyMap from '@/components/KeyMap'
import PageNotFound from '@/views/PageNotFound'
import PageForbidden from '@/views/PageForbidden'
import * as keymapService from '../services/keymapService/keymapService'
import ToastContainer from '@/components/ToastContainer'
import Sidebar from '@/components/sidebar/Sidebar'
import SpotlightSearch from '@/components/SpotlightSearch'
import Announcements from '@/components/Announcements'
import EventBus from '@/EventBus'

export default {
  name: 'OrgMain',
  props: ['orgId', 'subOrgId', 'teamId'],
  mixins: [EventBusMixin, UserListsMixin, TeamMixin],
  components: {
    NeedHelpBar,
    DemoOrgBar,
    DialogContainer,
    SubOrgSwitchHandler,
    TopImgBar,
    KeyMap,
    PageNotFound,
    ToastContainer,
    Sidebar,
    SpotlightSearch,
    Announcements,
    PageForbidden,
    ModalsContainer,
    QuickAddModal
  },
  data() {
    return {
      showKeyboardShortcuts: false,
      showSidebar: true,
      pageNotFound: false,
      pageForbidden: false,
      showSpotlightSearch: false,
      pageForbiddenMessage: undefined,
      USER_STATUS,
      quickAddModalShown: false,
      quickAddModalMounted: false,
      addInitiativePayload: null
    }
  },
  computed: {
    status() {
      return this.$store.getters.status
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    hasNewAnnouncements() {
      return this.$store.getters['announcement/hasNewAnnouncements']
    },
    user() {
      return this.$store.getters.user
    },
    selectedSubOrgId() {
      return this.$store.state.orgSelect.selectedOrgLikeTeamId
    }
  },
  methods: {
    showKeyMapModal() {
      this.showKeyboardShortcuts = true
    },
    goToOrgGoals() {
      const location = getTeamPageRouteConfig({
        orgId: this.orgId,
        entity: goalsTabIds.goals,
        teamId: this.selectedSubOrgId
      })
      this.$router.push(location)
    },
    goToYourGoals() {
      const location = getYourWorkPageRouteConfig({
        orgId: this.orgId,
        subOrgId: this.selectedSubOrgId
      })
      this.$router.push(location)
    },
    goToInbox() {
      const location = getInboxPageRouteConfig(this.orgId)
      this.$router.push(location)
    },
    submitForm() {
      EventBus.$emit('SUBMIT_FORM')
    },
    goBack() {
      const visitedRoutes = this.$store.state.visitedRoutes
      if (visitedRoutes.length > 1) {
        window.history.back()
      }
    },
    openSpotlightSearch(event) {
      if (this.showSpotlightSearch) {
        this.$refs.spotlightSearch.$refs.searchField.focus()
      } else {
        this.showSpotlightSearch = true
      }
      if (event && event.keyCode === KEY_CODES.FORWARD_SLASH) {
        event.preventDefault()
      }
    },
    fetchOrgCustomField() {
      this.status === USER_STATUS.LOGGED_IN &&
        this.$store.dispatch('getOrgCustomFields', { orgId: this.orgId })
    },
    onModalHide() {
      keymapService.removeEvent('MODAL_SHOWN')
    },
    onModalShown() {
      keymapService.addEvent('MODAL_SHOWN')
    },
    isRouteParamsInvalid() {
      const team = this.$store.getters.teamById(this.subOrgId)
      return !this.org || (this.subOrgId && (!team || team.organizationId !== this.orgId))
    },
    openAddInitiativeModal() {
      this.quickAddModalShown = true
      this.quickAddModalMounted = true
    },
    updateSelectedSubOrg() {
      let subOrgId
      if (this.subOrgId) {
        subOrgId = this.subOrgId
      } else if (this.teamId) {
        const teamId = this.teamId
        subOrgId = this.getClosestOrgLikeTeamId(teamId)
      }
      subOrgId && this.$store.dispatch('orgSelect/select', { subOrgId })
    },
    triggerAddInitiative(payload) {
      this.addInitiativePayload = payload
      this.openAddInitiativeModal()
    }
  },
  mounted() {
    if (this.isRouteParamsInvalid()) {
      this.pageNotFound = true
      return
    }
    this.updateSelectedSubOrg()
    keymapService.activateCategory('global', {
      'profile-menu': () => {
        const userInfo = R.path(['$refs', 'sidebar', '$refs', 'userInfo'], this)
        userInfo && userInfo.click()
      },
      escape: {
        callback: () =>
          !R.path(['activeElement', 'dataset', 'preventBlur'], document) &&
          document.activeElement.blur(),
        alwaysExecute: true
      },
      'view-shortcuts': this.showKeyMapModal,
      'navigate-to-org-goals': this.goToOrgGoals,
      'navigate-to-your-goals': this.goToYourGoals,
      'navigate-to-inbox': this.goToInbox,
      'submit-form': {
        callback: this.submitForm,
        alwaysExecute: true // handler will always be executed - even if modal/popover is open
      },
      'navigate-to-previous-page': this.goBack,
      'open-search': {
        callback: this.openSpotlightSearch,
        alwaysExecute: true
      },
      'quick-add-initiative': this.openAddInitiativeModal
    })
    this.listenToEventBus('SHOW_404', () => {
      this.pageNotFound = true
    })
    this.listenToEventBus('SHOW_403', ({ message }) => {
      this.pageForbidden = true
      this.pageForbiddenMessage = message
    })
    this.listenToEventBus('HIDE_SIDEBAR', () => {
      this.showSidebar = false
    })
    this.listenToEventBus('SHOW_SIDEBAR', () => {
      this.showSidebar = true
    })
    this.listenToEventBus(events.ADD_INITIATIVE, this.triggerAddInitiative)
    this.$root.$on('bv::popover::shown', ev => {
      const componentId = ev.vueTarget.id ? `_${ev.vueTarget.id}` : ''
      keymapService.addEvent(`POPOVER_SHOWN${componentId}`)
    })
    this.$root.$on('bv::popover::hidden', ev => {
      const componentId = ev.vueTarget.id ? `_${ev.vueTarget.id}` : ''
      keymapService.removeEvent(`POPOVER_SHOWN${componentId}`)
    })
    this.$root.$on('bv::modal::shown', this.onModalShown)
    this.$root.$on('bv::modal::hide', this.onModalHide)
    this.listenToEventBus(events.MODAL_MINIMIZE, this.onModalHide)
    this.listenToEventBus(events.MODAL_MAXIMIZE, this.onModalShown)
    this.listenToEventBus(events.OPEN_SPOTLIGHT_SEARCH, this.openSpotlightSearch)
    this.listenToEventBus(events.SHOW_KEYMAP_MODAL, this.showKeyMapModal)
    this.fetchOrgCustomField()
  },
  beforeDestroy() {
    keymapService.deactivateCategory('global')
  },
  watch: {
    $route() {
      if (this.pageNotFound) {
        this.pageNotFound = false
      }
      if (this.pageForbidden) {
        this.pageForbidden = false
      }
    },
    subOrgId: 'updateSelectedSubOrg',
    orgId() {
      this.updateSelectedSubOrg()
      this.fetchOrgCustomField()
    },
    status: 'fetchOrgCustomField',
    quickAddModalShown(isShown) {
      if (!isShown) {
        this.addInitiativePayload = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
main {
  min-width: 0;
}

.scrolling-box {
  overflow: auto;
  scrollbar-gutter: stable;
}
</style>
